import { pipe } from "fp-ts/lib/function";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import * as t from "io-ts";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import { uiStore } from "@/store/store-accessor";

async function getPagedBreakdancePostIds(page: number) {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_get_paginated_breakdance_post_ids",
      dataToPost: { page },
      resultKey: "postIds",
      decoder: t.array(t.number)
    }),
    fold(
      () => {
        return [];
      },
      postIds => {
        return postIds;
      }
    )
  );
}

async function getAllBreakdancePostIds() {
  uiStore.setIdsOfPostsToRegenerateCache([]);

  let postIds: number[] = [];
  let thereMorePostsToLoad = true;
  let currentPage = 0;

  while (thereMorePostsToLoad) {
    const postIdsForCurrentPage = await getPagedBreakdancePostIds(currentPage);
    currentPage++;

    postIds = [...postIds, ...postIdsForCurrentPage];

    if (postIdsForCurrentPage.length === 0) {
      thereMorePostsToLoad = false;
    }
  }

  return postIds;
}

export function regenerateGlobalSettingsCache() {
  // No need to wait for this to finish
  makeWordPressAjaxRequest({
    action: "breakdance_regenerate_global_settings_cache",
    decoder: t.unknown
  });
}

export async function regeneratePostsCache() {
  // Make sure the user gets a warning if they try to leave while we're regenerating the cache
  uiStore.setUnsavedChangesPresent(true);

  regenerateGlobalSettingsCache();

  const postIds = await getAllBreakdancePostIds();

  uiStore.setIdsOfPostsToRegenerateCache(postIds);

  for await (const id of postIds) {
    const postIdsMinusFirstOne = uiStore.idsOfPostsToRegenerateCache
      ? [...uiStore.idsOfPostsToRegenerateCache]
      : [];

    postIdsMinusFirstOne.shift();

    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_regenerate_post_cache",
        dataToPost: { id },
        decoder: t.unknown
      }),
      fold(
        () => {
          if (postIdsMinusFirstOne.length === 0) {
            uiStore.setIdsOfPostsToRegenerateCache(null);
          } else {
            uiStore.setIdsOfPostsToRegenerateCache(postIdsMinusFirstOne);
          }

          NotificationLogger.log.message(
            `Couldn't recreate the cache for post "${id}". Please re-save that post manually.`
          );
        },
        () => {
          if (postIdsMinusFirstOne.length === 0) {
            uiStore.setIdsOfPostsToRegenerateCache(null);

            NotificationLogger.log.message(
              "Finished recreating all posts cache"
            );
          } else {
            uiStore.setIdsOfPostsToRegenerateCache(postIdsMinusFirstOne);
          }
        }
      )
    );
  }
}
